import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Clock, X } from 'lucide-react';
import { useAdminCalendarEvents, useCreateAdminEvent, useUpdateAdminEvent, useDeleteAdminEvent } from '../../hooks/useAdminCalendar';
import { AdminEvent, ADMIN_EVENT_TYPE_COLORS, CreateAdminEventInput, UpdateAdminEventInput } from '../../types/adminCalendar';
import AdminAddEventModal from './AdminAddEventModal';

interface AdminCalendarProps {
  isDark: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}
function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
function isPastDate(date: Date, today: Date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d < t;
}
function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T09:00`;
}

const AdminCalendar: React.FC<AdminCalendarProps> = ({ isDark }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<AdminEvent | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [modalDefaultDate, setModalDefaultDate] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  const start = new Date(viewYear, viewMonth, 1).toISOString();
  const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59).toISOString();

  const { data: events = [], isLoading } = useAdminCalendarEvents(start, end);
  const createEvent = useCreateAdminEvent();
  const updateEvent = useUpdateAdminEvent();
  const deleteEvent = useDeleteAdminEvent();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const goToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };

  const eventsByDate = useMemo(() => {
    const map = new Map<string, AdminEvent[]>();
    events.forEach(ev => {
      const d = new Date(ev.event_date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    });
    return map;
  }, [events]);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return events
      .filter(e => new Date(e.event_date) >= now)
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
      .slice(0, 5);
  }, [events]);

  const selectedDayEvents = useMemo(() => {
    if (!selectedDay) return [];
    const key = `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`;
    return eventsByDate.get(key) || [];
  }, [selectedDay, eventsByDate]);

  const handleCreate = (input: CreateAdminEventInput) => createEvent.mutate(input);
  const handleUpdate = (id: string, input: UpdateAdminEventInput) => updateEvent.mutate({ id, input });
  const handleDelete = (id: string) => { deleteEvent.mutate(id); setEditingEvent(null); };

  const openNewEvent = (defaultDate?: Date) => {
    setEditingEvent(null);
    setModalDefaultDate(defaultDate ? formatDateForInput(defaultDate) : '');
    setIsModalOpen(true);
    closePopover();
  };
  const openEditEvent = (ev: AdminEvent) => {
    setEditingEvent(ev);
    setModalDefaultDate('');
    setIsModalOpen(true);
    closePopover();
  };
  const closePopover = useCallback(() => { setSelectedDay(null); setPopoverPos(null); }, []);

  const handleDayClick = (date: Date, e: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedDay && isSameDay(date, selectedDay)) { closePopover(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    let top = rect.bottom + 4, left = rect.left;
    if (left + 260 > vw) left = vw - 270;
    if (left < 10) left = 10;
    if (top + 200 > vh) top = rect.top - 204;
    setSelectedDay(date);
    setPopoverPos({ top, left });
  };

  useEffect(() => {
    if (!popoverPos) return;
    const handleClick = (e: MouseEvent) => { if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) closePopover(); };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') closePopover(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleEscape); };
  }, [popoverPos, closePopover]);

  const borderCls = isDark ? 'border-white/10' : 'border-slate-200';
  const cardBg = isDark ? 'bg-white/5' : 'bg-white shadow-sm';

  return (
    <div className={`backdrop-blur-sm rounded-2xl border overflow-hidden transition-colors ${cardBg} ${borderCls}`}>
      {/* Header */}
      <div className={`p-5 border-b ${borderCls}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Admin Calendar</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Payments, taxes & deadlines</p>
            </div>
          </div>
          <button onClick={() => openNewEvent()} className="p-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90 transition-opacity">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button onClick={prevMonth} className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h4 className={`text-sm font-semibold min-w-[130px] text-center ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </h4>
            <button onClick={nextMonth} className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button onClick={goToday} className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
            Today
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div>
        {/* Weekday headers */}
        <div className={`grid grid-cols-7 border-b ${borderCls}`}>
          {WEEKDAYS.map(d => (
            <div key={d} className={`py-1.5 text-center text-[9px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {d}
            </div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`pad-${i}`} className={`h-12 border-b border-r ${borderCls} ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50/50'}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const date = new Date(viewYear, viewMonth, day);
            const key = `${viewYear}-${viewMonth}-${day}`;
            const dayEvents = eventsByDate.get(key) || [];
            const isToday = isSameDay(date, today);
            const past = isPastDate(date, today);
            const isSelected = selectedDay && isSameDay(date, selectedDay);

            return (
              <button
                key={day}
                onClick={(e) => handleDayClick(date, e)}
                className={`h-12 border-b border-r ${borderCls} p-1 text-left flex flex-col transition-colors ${
                  past ? 'opacity-30 cursor-default' :
                  isSelected
                    ? isDark ? 'bg-cyan-500/10 ring-1 ring-inset ring-cyan-500/30' : 'bg-cyan-50 ring-1 ring-inset ring-cyan-300'
                    : isDark ? 'hover:bg-white/5 cursor-pointer' : 'hover:bg-slate-50 cursor-pointer'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-medium ${
                  isToday
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                    : past ? (isDark ? 'text-slate-600' : 'text-slate-300') : (isDark ? 'text-slate-300' : 'text-slate-700')
                }`}>
                  {day}
                </span>
                {dayEvents.length > 0 && (
                  <div className="flex gap-0.5 mt-auto">
                    {dayEvents.slice(0, 3).map(ev => (
                      <span
                        key={ev.id}
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: ADMIN_EVENT_TYPE_COLORS[ev.event_type as keyof typeof ADMIN_EVENT_TYPE_COLORS] || '#6b7280' }}
                      />
                    ))}
                    {dayEvents.length > 3 && <span className={`text-[7px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>+{dayEvents.length - 3}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Upcoming */}
      <div className={`p-4 border-t ${borderCls}`}>
        <h4 className={`text-xs font-medium mb-2 flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          <Clock className="h-3.5 w-3.5 text-cyan-400" /> Upcoming
        </h4>
        {isLoading ? (
          <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Loading...</p>
        ) : upcomingEvents.length === 0 ? (
          <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No upcoming events</p>
        ) : (
          <div className="space-y-1.5">
            {upcomingEvents.map(ev => (
              <button
                key={ev.id}
                onClick={() => openEditEvent(ev)}
                className={`w-full flex items-center gap-2 py-1.5 px-2 -mx-2 rounded-lg transition-colors text-left ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ADMIN_EVENT_TYPE_COLORS[ev.event_type as keyof typeof ADMIN_EVENT_TYPE_COLORS] || '#6b7280' }} />
                <span className={`flex-1 text-xs truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{ev.title}</span>
                <span className={`text-[10px] flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Day popover */}
      {selectedDay && popoverPos && (
        <div
          ref={popoverRef}
          className={`fixed z-40 w-64 rounded-xl shadow-2xl border overflow-hidden animate-in fade-in zoom-in-95 duration-150 ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <div className={`flex items-center justify-between px-3 py-2.5 border-b ${borderCls}`}>
            <h4 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </h4>
            <button onClick={closePopover} className={`p-1 rounded-md transition-colors ${isDark ? 'hover:bg-white/5 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-3 py-2 max-h-48 overflow-y-auto">
            {selectedDayEvents.length === 0 ? (
              <p className={`text-[11px] py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No events on this day.</p>
            ) : (
              <div className="space-y-1.5">
                {selectedDayEvents.map(ev => (
                  <button key={ev.id} onClick={() => openEditEvent(ev)} className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ADMIN_EVENT_TYPE_COLORS[ev.event_type as keyof typeof ADMIN_EVENT_TYPE_COLORS] || '#6b7280' }} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>{ev.title}</p>
                      <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {new Date(ev.event_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isPastDate(selectedDay, today) && (
            <div className={`px-3 py-2 border-t ${borderCls}`}>
              <button
                onClick={() => openNewEvent(selectedDay)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-xs font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Event
              </button>
            </div>
          )}
        </div>
      )}

      <AdminAddEventModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingEvent(null); setModalDefaultDate(''); }}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        editingEvent={editingEvent}
        defaultDate={modalDefaultDate}
      />
    </div>
  );
};

export default AdminCalendar;
